/**
 * BankTransferModal — QR VietQR + thong tin chuyen khoan
 * Thay the MomoPayment.tsx
 * VietQR Quick Link: https://img.vietqr.io/image/<BANK>-<STK>-<template>.png
 */

import React, { useState, useEffect } from 'react';
import { X, Copy, CheckCircle, Clock, Sparkles, AlertCircle, AlertTriangle, PartyPopper } from 'lucide-react';
import { formatVnd } from '../../services/mock/creditMock';

// ===== CONFIG: Thong tin ngan hang nhan tien =====
const BANK_CONFIG = {
  bankId: '970403',             // Sacombank BIN code (VietQR accepted)
  accountNo: '060950198888',    // So tai khoan Sacombank
  accountName: 'CO VAN TAN',    // Ten chu tai khoan (IN HOA, khong dau)
  bankDisplayName: 'Sacombank', // Ten hien thi
};

interface BankTransferModalProps {
  isOpen: boolean;
  purchaseData: {
    id: string;
    transfer_code: string;
    credits_total: number;
    price_vnd: number;
    expires_at?: string;
    reused?: boolean;
  } | null;
  accessToken?: string;
  onClose: () => void;
  onConfirmPaid: () => void;
  onConfirmed?: () => void; // callback khi payment_status='paid' (Phase 3 polling)
}

const POLL_INTERVAL_MS = 10_000;

function formatMMSS(ms: number): string {
  if (ms <= 0) return '00:00';
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60).toString().padStart(2, '0');
  const s = (total % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function buildVietQRUrl(amount: number, transferCode: string): string {
  const { bankId, accountNo, accountName } = BANK_CONFIG;
  return `https://img.vietqr.io/image/${bankId}-${accountNo}-qr_only.png`
    + `?amount=${amount}`
    + `&addInfo=${encodeURIComponent(transferCode)}`
    + `&accountName=${encodeURIComponent(accountName)}`;
}

const BankTransferModal: React.FC<BankTransferModalProps> = ({
  isOpen,
  purchaseData,
  accessToken,
  onClose,
  onConfirmPaid,
  onConfirmed,
}) => {
  const [step, setStep] = useState<'qr' | 'confirming' | 'done' | 'expired' | 'partial' | 'overpaid' | 'too_small' | 'unknown_dtl'>('qr');
  const [copied, setCopied] = useState<string | null>(null);
  const [qrError, setQrError] = useState(false);
  const [remainingMs, setRemainingMs] = useState<number>(0);
  const [autoCloseMs, setAutoCloseMs] = useState<number>(0);
  const [resultInfo, setResultInfo] = useState<{ title: string; body: string; creditsAdded?: number } | null>(null);

  const expiresAtMs = purchaseData?.expires_at ? new Date(purchaseData.expires_at).getTime() : 0;

  // Countdown hết hạn 15 phút
  useEffect(() => {
    if (!isOpen || !expiresAtMs) return;
    const tick = () => {
      const ms = expiresAtMs - Date.now();
      setRemainingMs(ms);
      if (ms <= 0 && step === 'qr') {
        setStep('expired');
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isOpen, expiresAtMs, step]);

  // Phase 3: poll payment status mỗi 10s để detect auto-confirm từ Email Worker
  useEffect(() => {
    if (!isOpen || !purchaseData || !accessToken) return;
    if (step !== 'qr' && step !== 'confirming') return;

    const workersUrl = import.meta.env.VITE_WORKERS_URL || 'http://localhost:8787';
    const purchaseId = purchaseData.id;

    const poll = async () => {
      try {
        const res = await fetch(`${workersUrl}/api/credits/purchase/${purchaseId}/status`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) return;
        const { purchase, exception, latestNotif } = await res.json();

        // Paid với notification (partial hoặc full)
        if (purchase.payment_status === 'paid') {
          if (latestNotif?.type === 'payment_partial') {
            setStep('partial');
            setResultInfo({
              title: latestNotif.title,
              body: latestNotif.body,
              creditsAdded: latestNotif.metadata?.credits_added ?? 0,
            });
          } else {
            setStep('done');
          }
          onConfirmed?.();
          return;
        }

        // Exception pending (overpaid / too_small / unknown_dtl)
        if (exception) {
          const typeMap: Record<string, 'overpaid' | 'too_small' | 'unknown_dtl' | null> = {
            amount_overpaid: 'overpaid',
            amount_too_small: 'too_small',
            unknown_dtl: 'unknown_dtl',
          };
          const newStep = typeMap[exception.exception_type];
          if (newStep && latestNotif) {
            setStep(newStep);
            setResultInfo({ title: latestNotif.title, body: latestNotif.body });
            return;
          }
        }

        // Expired
        if (purchase.payment_status === 'expired') {
          setStep('expired');
        }
      } catch {
        // network error — continue polling, non-fatal
      }
    };

    // Poll ngay lần đầu + interval
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isOpen, purchaseData, accessToken, step, onConfirmed]);

  // Auto-close 3s sau khi hiện step 'done'
  useEffect(() => {
    if (step !== 'done') return;
    setAutoCloseMs(3000);
    const countdown = setInterval(() => {
      setAutoCloseMs(prev => {
        if (prev <= 1000) {
          clearInterval(countdown);
          setStep('qr');
          setQrError(false);
          onClose();
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);
    return () => clearInterval(countdown);
  }, [step, onClose]);

  if (!isOpen || !purchaseData) return null;

  const { transfer_code, price_vnd, credits_total, reused } = purchaseData;
  const qrUrl = buildVietQRUrl(price_vnd, transfer_code);
  const countdownLabel = expiresAtMs ? formatMMSS(remainingMs) : '';
  const countdownWarn = remainingMs > 0 && remainingMs < 3 * 60 * 1000; // < 3 phút cảnh báo

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleConfirmPaid = () => {
    setStep('confirming');
    // UI: hien trang thai cho xac nhan
    // Backend: admin hoac Apps Script se confirm
    onConfirmPaid();
  };

  const handleClose = () => {
    setStep('qr');
    setQrError(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-[2rem] shadow-[0_20px_60px_-15px_rgba(0,0,0,0.5)] w-full max-w-4xl overflow-hidden flex flex-col max-h-[95vh] sm:max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-4 bg-emerald-50 border-b border-emerald-100 flex items-center justify-between shrink-0 relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center shadow-sm">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-emerald-900 tracking-wide">Thanh toán tự động</h3>
          </div>
          <button onClick={handleClose} className="p-2.5 rounded-xl hover:bg-emerald-100/50 transition-colors">
            <X className="w-5 h-5 text-emerald-700/60 hover:text-emerald-700" />
          </button>
        </div>

        <div className="p-5 md:p-6 lg:p-7 overflow-y-auto scrollbar-thin relative bg-slate-50/50">
          {/* Subtle background glow */}
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-3/4 h-1/2 bg-emerald-400/5 blur-[100px] pointer-events-none rounded-full"></div>
          {step === 'qr' && (
            <div className="relative z-10">
              {reused && (
                <div className="mb-4 px-4 py-3 rounded-2xl bg-indigo-50/80 backdrop-blur-sm border border-indigo-100 flex items-start gap-3 shadow-sm">
                  <AlertCircle className="w-5 h-5 text-indigo-500 shrink-0 mt-0.5" />
                  <p className="text-[13px] font-medium text-indigo-700 leading-relaxed">
                    Bạn có đơn đang chờ thanh toán. Chúng tôi hiển thị lại mã QR của đơn này. Hệ thống vẫn đang tự động theo dõi.
                  </p>
                </div>
              )}

              {/* Amount + credits info */}
              <div className="flex flex-col items-center justify-center mb-6">
                <div className="px-6 py-4 bg-white rounded-3xl shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-100 flex flex-wrap items-center justify-center gap-4 hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.08)] transition-all">
                  <span className="text-3xl font-black text-slate-800 tracking-tighter">{formatVnd(price_vnd)}</span>
                  <div className="hidden sm:block w-px h-8 bg-slate-200"></div>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                    <span className="text-2xl font-black text-emerald-600 tracking-tight">+{credits_total.toLocaleString()} <span className="text-[15px] font-bold text-emerald-600/70">credits</span></span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-5 lg:gap-6">
                
                {/* LEFT SIDE: QR CODE */}
                <div className="flex-[0.9] flex flex-col items-center bg-white p-5 rounded-3xl border border-slate-100 shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-emerald-400 to-teal-400"></div>
                  
                  <div className="w-full flex items-center gap-3 mb-4 mt-1">
                    <div className="h-px bg-slate-200 flex-1"></div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest text-center">
                      Cách 1: Quét mã QR
                    </p>
                    <div className="h-px bg-slate-200 flex-1"></div>
                  </div>

                  <div className="relative w-[180px] h-[180px] lg:w-[200px] lg:h-[200px] bg-white rounded-2xl flex items-center justify-center p-0 mb-4 shrink-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden hover:scale-[1.02] transition-transform duration-300">
                    {qrError ? (
                      <div className="text-center p-4">
                        <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
                        <p className="text-sm font-bold text-slate-700">Không tải được QR</p>
                        <p className="text-[11px] text-slate-500 mt-1">Vui lòng chuyển thủ công bên phải</p>
                      </div>
                    ) : (
                      <img
                        src={qrUrl}
                        alt="QR Chuyển khoản"
                        className="w-full h-full object-contain"
                        onError={() => setQrError(true)}
                      />
                    )}
                  </div>

                  {countdownLabel && (
                    <div className={`flex items-center justify-center gap-2 px-4 py-2 rounded-xl border ${countdownWarn ? 'bg-rose-50 text-rose-600 border-rose-100' : 'bg-slate-50/80 text-slate-500 border-slate-100'}`}>
                      <Clock className={`w-4 h-4 ${countdownWarn ? 'animate-pulse' : ''}`} />
                      <span className="text-[13px] font-mono font-bold tracking-wide">Hết hạn sau {countdownLabel}</span>
                    </div>
                  )}

                  <p className="text-[12px] text-center text-slate-400 mt-4 font-medium max-w-[200px] leading-relaxed">
                    Sử dụng App ngân hàng để quét mã này.
                  </p>
                </div>

                {/* Vertical Divider for desktop */}
                <div className="hidden md:block w-px bg-slate-100 self-stretch"></div>

                {/* RIGHT SIDE: MANUAL INFO */}
                <div className="flex-[1.1] flex flex-col justify-center bg-white p-5 md:p-6 rounded-3xl border border-slate-100 shadow-sm relative overflow-hidden">
                  <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-400 to-blue-400"></div>

                  <div className="w-full flex items-center gap-3 mb-4 mt-1 md:mt-0">
                    <div className="h-px bg-slate-200 flex-1"></div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-widest text-center">
                      Cách 2: Chuyển thủ công
                    </p>
                    <div className="h-px bg-slate-200 flex-1"></div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <InfoRow
                      label="Ngân hàng"
                      value={BANK_CONFIG.bankDisplayName}
                    />
                    <InfoRow
                      label="Số tài khoản"
                      value={BANK_CONFIG.accountNo}
                      onCopy={() => handleCopy(BANK_CONFIG.accountNo, 'stk')}
                      isCopied={copied === 'stk'}
                    />
                    <InfoRow
                      label="Chủ tài khoản"
                      value={BANK_CONFIG.accountName}
                    />
                  </div>
                  
                  {/* Highlighted Transfer Code */}
                  <div className="p-4 bg-emerald-50/50 border border-emerald-100 rounded-2xl relative mt-2 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)]">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500 rounded-l-2xl"></div>
                    <div className="pl-3 flex flex-col gap-3">
                      <div>
                        <p className="text-[13px] font-bold text-slate-700 mb-1">Nội dung chuyển khoản</p>
                        <div className="flex items-start gap-1.5 bg-amber-50/80 p-2.5 rounded-xl border border-amber-100/50">
                          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                          <p className="text-[12px] font-semibold text-amber-700/90 leading-relaxed">
                            Bắt buộc ghi chính xác nội dung này để hệ thống tự động ghi nhận giao dịch.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3 bg-white p-2.5 rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                        <span className="font-mono font-black text-2xl text-emerald-700 tracking-widest pl-3">
                          {transfer_code}
                        </span>
                        <button 
                          onClick={() => handleCopy(transfer_code, 'code')}
                          className="p-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-all shadow-md shadow-emerald-600/20 active:scale-95 shrink-0"
                          title="Copy mã"
                        >
                          {copied === 'code' ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Note about auto-confirm */}
              <div className="mt-5 flex flex-col items-center pb-2">
                <div className="flex items-center gap-2.5 text-emerald-700 bg-emerald-50/80 px-5 py-2.5 rounded-full border border-emerald-100/50 shadow-sm backdrop-blur-sm">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-600"></span>
                  </span>
                  <span className="text-[13px] font-bold tracking-tight">Hệ thống đang tự động chờ nhận tiền...</span>
                </div>
              </div>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center py-10 relative">
              {/* Confetti or decorative background elements */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <Sparkles className="absolute top-2 left-10 w-6 h-6 text-yellow-400 animate-pulse" />
                <Sparkles className="absolute bottom-10 right-10 w-5 h-5 text-emerald-400 animate-pulse" style={{ animationDelay: '0.5s' }} />
                <PartyPopper className="absolute top-5 right-12 w-6 h-6 text-indigo-400 opacity-50 transform rotate-12" />
              </div>
              
              <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-tr from-emerald-400 to-emerald-300 rounded-full flex items-center justify-center relative shadow-xl shadow-emerald-500/20">
                <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-30" style={{ animationDuration: '2s' }}></div>
                <CheckCircle className="w-12 h-12 text-white relative z-10" />
              </div>
              
              <h4 className="text-2xl font-black text-slate-800 mb-2 tracking-tight">Tuyệt vời!</h4>
              <p className="text-slate-500 font-medium mb-6">Thanh toán của bạn đã được xác nhận</p>
              
              <div className="bg-emerald-50/80 backdrop-blur-sm text-emerald-800 px-5 py-3 rounded-2xl inline-flex flex-col items-center justify-center mb-8 border border-emerald-100 shadow-sm">
                <span className="text-xs font-bold uppercase tracking-widest text-emerald-600/80 mb-1">Đã cộng vào tài khoản</span>
                <div className="flex items-baseline gap-1.5">
                  <strong className="text-4xl font-black text-emerald-600">+{credits_total.toLocaleString()}</strong>
                  <span className="text-sm font-bold text-emerald-700">credits</span>
                </div>
              </div>
              
              {autoCloseMs > 0 && (
                <p className="text-xs font-medium text-slate-400 mb-5 flex items-center justify-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Cửa sổ sẽ tự động đóng sau {Math.ceil(autoCloseMs / 1000)}s...
                </p>
              )}
              
              <button
                onClick={handleClose}
                className="w-full py-4 rounded-xl text-base font-bold text-white bg-slate-900 hover:bg-slate-800 transition-all active:scale-95 shadow-xl shadow-slate-900/20 relative z-10"
              >
                Tiếp tục trải nghiệm
              </button>
            </div>
          )}

          {step === 'expired' && (
            <div className="text-center py-10">
              <div className="w-16 h-16 mx-auto mb-4 bg-rose-50 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-rose-500" />
              </div>
              <h4 className="text-lg font-bold text-slate-800 mb-2">Mã QR đã hết hạn</h4>
              <p className="text-sm text-slate-500 mb-1">
                Đơn này đã hết hiệu lực do không thanh toán trong 15 phút.
              </p>
              <p className="text-xs font-medium text-slate-400 mb-6">
                Vui lòng đóng thông báo này và tạo lại đơn mới.
              </p>
              <button
                onClick={handleClose}
                className="w-full px-6 py-3.5 rounded-xl text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all active:scale-95"
              >
                Đóng
              </button>
            </div>
          )}

          {step === 'partial' && resultInfo && (
            <div className="text-center py-10">
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-50 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-amber-600" />
              </div>
              <h4 className="text-lg font-bold text-amber-700 mb-2">{resultInfo.title}</h4>
              {resultInfo.creditsAdded && (
                <p className="text-2xl font-black text-emerald-600 mb-2">
                  +{resultInfo.creditsAdded.toLocaleString()} credits
                </p>
              )}
              <p className="text-sm text-slate-600 mb-6 leading-relaxed">{resultInfo.body}</p>
              <button
                onClick={handleClose}
                className="w-full px-6 py-3.5 rounded-xl text-sm font-bold text-white bg-emerald-600 hover:bg-emerald-700 transition-all active:scale-95"
              >
                Tiếp tục
              </button>
            </div>
          )}

          {step === 'overpaid' && resultInfo && (
            <div className="text-center py-10">
              <div className="w-16 h-16 mx-auto mb-4 bg-amber-50 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>
              <h4 className="text-lg font-bold text-amber-700 mb-2">{resultInfo.title}</h4>
              <p className="text-sm text-slate-600 mb-6 leading-relaxed px-2">{resultInfo.body}</p>
              <button
                onClick={handleClose}
                className="w-full px-6 py-3.5 rounded-xl text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all active:scale-95"
              >
                Đóng — sẽ chat support sau
              </button>
            </div>
          )}

          {step === 'too_small' && resultInfo && (
            <div className="text-center py-10">
              <div className="w-16 h-16 mx-auto mb-4 bg-rose-50 rounded-full flex items-center justify-center">
                <X className="w-8 h-8 text-rose-500" />
              </div>
              <h4 className="text-lg font-bold text-rose-700 mb-2">{resultInfo.title}</h4>
              <p className="text-sm text-slate-600 mb-6 leading-relaxed px-2">{resultInfo.body}</p>
              <button
                onClick={handleClose}
                className="w-full px-6 py-3.5 rounded-xl text-sm font-bold text-white bg-rose-600 hover:bg-rose-700 transition-all active:scale-95"
              >
                Đóng — liên hệ support
              </button>
            </div>
          )}

          {step === 'unknown_dtl' && resultInfo && (
            <div className="text-center py-10">
              <div className="w-16 h-16 mx-auto mb-4 bg-orange-50 rounded-full flex items-center justify-center">
                <AlertCircle className="w-8 h-8 text-orange-500" />
              </div>
              <h4 className="text-lg font-bold text-orange-700 mb-2">{resultInfo.title}</h4>
              <p className="text-sm text-slate-600 mb-6 leading-relaxed px-2">{resultInfo.body}</p>
              <button
                onClick={handleClose}
                className="w-full px-6 py-3.5 rounded-xl text-sm font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 transition-all active:scale-95"
              >
                Đóng
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ===== Sub-component: Info row =====
function InfoRow({
  label,
  value,
  onCopy,
  isCopied,
}: {
  label: string;
  value: string;
  onCopy?: () => void;
  isCopied?: boolean;
}) {
  return (
    <div className="flex justify-between items-center p-3 rounded-xl bg-slate-50 border border-slate-100">
      <span className="text-slate-500 text-sm font-medium">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-mono font-bold text-base text-slate-800 tracking-tight">
          {value}
        </span>
        {onCopy && (
          <button 
            onClick={onCopy} 
            className="p-2 hover:bg-slate-200 rounded-md transition-colors"
            title="Copy"
          >
            {isCopied
              ? <CheckCircle className="w-4 h-4 text-emerald-600" />
              : <Copy className="w-4 h-4 text-slate-400 hover:text-slate-600" />
            }
          </button>
        )}
      </div>
    </div>
  );
}

export default BankTransferModal;
